import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import DialogActions from "@mui/material/DialogActions";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Typography from "@mui/material/Typography";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CollectionsBookmarkOutlinedIcon from "@mui/icons-material/CollectionsBookmarkOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TextField from "../Form/TextField";
import IconButton from "../Buttons/IconButton";
import ModalBasicLayout from "./ModalBasicLayout";
import ContainerModalActions from "../Containers/ContainerModalActions";
import Loader from "../Loaders/Loader";
import { ConfirmAlert, SimpleAlert } from "../Alerts/Alerts";
import { useDispatchApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import { getPathDatabaseByRole } from "../../actions/auth";
import { canWriteCreativeTemplates } from "../../features/creativeTemplates/utils/permissions";
import {
  startDeleteAnimateImagePrompt,
  startListAnimateImagePrompts,
  startSaveAnimateImagePrompt,
} from "../../actions/animateImageLibrary";
import {
  BUTTON_CANCEL,
  BUTTON_DELETE,
  BUTTON_GO_BACK,
  BUTTON_SAVE,
  FIELD_NUMBER_GREATER_THAN,
  MODAL_ANIMATE_IMAGE_PROMPT_HELPER,
  MODAL_ANIMATE_IMAGE_PROMPT_PLACEHOLDER,
  NAME,
  TEMPLATES_DELETE_PROMPT,
  TEMPLATES_DELETE_PROMPT_ERROR,
  TEMPLATES_IMPORT_PROMPT,
  TEMPLATES_IMPORT_PROMPT_EMPTY,
  TEMPLATES_IMPORT_PROMPT_ERROR,
  TEMPLATES_IMPORT_PROMPT_TITLE,
  TEMPLATES_IMPORT_SELECTED,
  TEMPLATES_PROMPT,
  TEMPLATES_SAVE_PROMPT,
  TEMPLATES_SAVE_PROMPT_ERROR,
  TEMPLATES_SAVE_PROMPT_SUCCESS,
  TEMPLATES_SAVE_PROMPT_TITLE,
} from "../../i18n/keysTranslations";
import { textFieldInputSx } from "../../styles";
import { BORDER_RADIUS } from "../../utils/constants";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import {
  ANIMATE_IMAGE_PROMPT_MAX_LENGTH,
  ANIMATE_IMAGE_PROMPT_NAME_MAX_LENGTH,
} from "../../utils/animateImageConstants";

const optionalRules = { required: false };

const AnimateImagePromptField = ({
  control,
  errors,
  disabled = false,
  promptValue = "",
  onApplyPrompt,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { role } = dispatch(getPathDatabaseByRole());
  const canWrite = canWriteCreativeTemplates({ role });
  const importRequestRef = useRef(0);
  const [isSaveOpen, setIsSaveOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [savedPrompts, setSavedPrompts] = useState([]);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(false);
  const [promptsLoadFailed, setPromptsLoadFailed] = useState(false);
  const [deletingID, setDeletingID] = useState("");
  const [selectedPromptID, setSelectedPromptID] = useState("");

  const {
    control: saveControl,
    handleSubmit,
    reset: resetSave,
    watch,
    formState: { errors: saveErrors },
  } = useForm({
    defaultValues: {
      name: "",
      prompt: "",
    },
  });
  const saveName = watch("name");
  const savePrompt = watch("prompt");
  const canSave =
    Boolean(String(saveName || "").trim()) &&
    Boolean(String(savePrompt || "").trim());
  const promptError = errors?.prompt;
  const currentLength = String(promptValue || "").length;

  const openSave = () => {
    resetSave({
      name: "",
      prompt: String(promptValue || ""),
    });
    setIsSaveOpen(true);
  };

  const onSave = async ({ name, prompt }) => {
    const result = await dispatch(
      startSaveAnimateImagePrompt({ name, prompt })
    );
    if (!result?.ok) {
      SimpleAlert({
        title: t(TEMPLATES_SAVE_PROMPT_ERROR),
        icon: "error",
      });
      return;
    }
    setIsSaveOpen(false);
    SimpleAlert({
      title: t(TEMPLATES_SAVE_PROMPT_SUCCESS),
      icon: "success",
    });
  };

  const openImport = async () => {
    const requestID = importRequestRef.current + 1;
    importRequestRef.current = requestID;
    setIsImportOpen(true);
    setIsLoadingPrompts(true);
    setPromptsLoadFailed(false);
    setSelectedPromptID("");
    setSavedPrompts([]);
    const result = await dispatch(startListAnimateImagePrompts());
    if (importRequestRef.current !== requestID) return;
    setIsLoadingPrompts(false);
    if (!result?.ok) {
      setPromptsLoadFailed(true);
      SimpleAlert({
        title: t(TEMPLATES_IMPORT_PROMPT_ERROR),
        icon: "error",
      });
      return;
    }
    setSavedPrompts(result.prompts || []);
  };

  const onImportSelected = () => {
    const item = savedPrompts.find((entry) => entry.id === selectedPromptID);
    if (!item) return;
    onApplyPrompt(item.prompt);
    setIsImportOpen(false);
  };

  const onDelete = async (event, promptID) => {
    event.stopPropagation();
    if (deletingID) return;
    const confirmed = await ConfirmAlert({
      title: t(TEMPLATES_DELETE_PROMPT),
      confirmButtonText: t(BUTTON_DELETE),
    });
    if (!confirmed?.isConfirmed) return;
    setDeletingID(promptID);
    const result = await dispatch(startDeleteAnimateImagePrompt({ promptID }));
    setDeletingID("");
    if (!result?.ok) {
      SimpleAlert({
        title: t(TEMPLATES_DELETE_PROMPT_ERROR),
        icon: "error",
      });
      return;
    }
    setSavedPrompts((current) =>
      current.filter((item) => item.id !== promptID)
    );
    setSelectedPromptID((current) => (current === promptID ? "" : current));
  };

  return (
    <>
      <Box
        sx={{
          ...textFieldInputSx["& .MuiInputBase-root"],
          overflow: "hidden",
          flexShrink: 0,
          pr: "8px",
          pb: "6px",
          ...(promptError ? { borderColor: "error.main" } : {}),
        }}
      >
        <TextField
          control={control}
          errors={{}}
          name="prompt"
          variant="filled"
          fullWidth
          multiline
          minRows={3}
          maxRows={6}
          placeholder={t(MODAL_ANIMATE_IMAGE_PROMPT_PLACEHOLDER)}
          disabled={disabled}
          rules={{
            maxLength: {
              value: ANIMATE_IMAGE_PROMPT_MAX_LENGTH,
              message: t(FIELD_NUMBER_GREATER_THAN, {
                value: ANIMATE_IMAGE_PROMPT_MAX_LENGTH,
              }),
            },
            required: false,
          }}
          sx={{
            "& .MuiInputBase-root.MuiFilledInput-root": {
              ...textFieldInputSx["& .MuiInputBase-root"],
              backgroundColor: "transparent",
              border: "none",
              boxShadow: "none",
              "&:hover, &.Mui-focused, &.Mui-disabled": {
                backgroundColor: "transparent",
              },
              "&:before, &:after": {
                display: "none",
              },
            },
            "& .MuiInputBase-inputMultiline": {
              resize: "none",
              backgroundColor: "transparent",
            },
          }}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-end",
            flexShrink: 0,
            pr: "4px",
          }}
        >
          <IconButton
            tooltipText={TEMPLATES_SAVE_PROMPT}
            disabled={disabled || !canWrite}
            edge={false}
            onClick={openSave}
            sx={{ p: "2px", width: 28, height: 28 }}
          >
            <SaveOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton
            tooltipText={TEMPLATES_IMPORT_PROMPT}
            disabled={disabled}
            edge={false}
            onClick={openImport}
            sx={{ p: "2px", width: 28, height: 28 }}
          >
            <CollectionsBookmarkOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          mt: 0.5,
        }}
      >
        <Typography
          variant="caption"
          color={promptError ? "error" : "text.secondary"}
        >
          {promptError?.message || t(MODAL_ANIMATE_IMAGE_PROMPT_HELPER)}
        </Typography>
        <Typography
          variant="caption"
          color={
            currentLength > ANIMATE_IMAGE_PROMPT_MAX_LENGTH
              ? "error"
              : "text.secondary"
          }
          sx={{ flexShrink: 0 }}
        >
          {currentLength}/{ANIMATE_IMAGE_PROMPT_MAX_LENGTH}
        </Typography>
      </Box>
      <ModalBasicLayout
        modalOpen={isSaveOpen}
        onCloseModal={() => setIsSaveOpen(false)}
        title={t(TEMPLATES_SAVE_PROMPT_TITLE)}
        ariaLabel={t(TEMPLATES_SAVE_PROMPT_TITLE)}
        maxWidth="sm"
        fullWidth
        disableScrollLock
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            name="name"
            control={saveControl}
            errors={saveErrors}
            label={t(NAME)}
            variant="filled"
            fullWidth
            autoFocus
            rules={optionalRules}
            inputProps={{ maxLength: ANIMATE_IMAGE_PROMPT_NAME_MAX_LENGTH }}
          />
          <TextField
            name="prompt"
            control={saveControl}
            errors={saveErrors}
            label={t(TEMPLATES_PROMPT)}
            variant="filled"
            fullWidth
            multiline
            minRows={4}
            rules={optionalRules}
            inputProps={{ maxLength: ANIMATE_IMAGE_PROMPT_MAX_LENGTH }}
          />
        </Box>
        <DialogActions sx={{ px: { xs: 3, sm: 5 }, pb: 3, mt: 0.5 }}>
          <ContainerModalActions
            hasPadding={false}
            propsSecondaryButton={{
              showButton: true,
              text: BUTTON_CANCEL,
              onClick: () => setIsSaveOpen(false),
            }}
            propsPrimaryButton={{
              showButton: true,
              text: BUTTON_SAVE,
              disabled: !canSave,
              onClick: handleSubmit(onSave),
            }}
          />
        </DialogActions>
      </ModalBasicLayout>
      <ModalBasicLayout
        modalOpen={isImportOpen}
        onCloseModal={() => setIsImportOpen(false)}
        title={t(TEMPLATES_IMPORT_PROMPT_TITLE)}
        ariaLabel={t(TEMPLATES_IMPORT_PROMPT_TITLE)}
        maxWidth="sm"
        fullWidth
        disableScrollLock
      >
        {isLoadingPrompts ? (
          <Loader hasMarginTop={false} size={28} fullWidth />
        ) : null}
        {!isLoadingPrompts && !promptsLoadFailed && !savedPrompts.length ? (
          <Typography variant="body2" color="text.secondary">
            {t(TEMPLATES_IMPORT_PROMPT_EMPTY)}
          </Typography>
        ) : null}
        {!isLoadingPrompts && savedPrompts.length ? (
          <RadioGroup
            value={selectedPromptID}
            onChange={(event) => setSelectedPromptID(event.target.value)}
          >
            {savedPrompts.map((item) => (
              <Box
                key={item.id}
                sx={{
                  mb: 1,
                  p: 1.5,
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                  borderRadius: BORDER_RADIUS,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 0.5,
                }}
              >
                <FormControlLabel
                  value={item.id}
                  control={
                    <Radio
                      sx={{
                        p: 0,
                        mr: 1,
                        mt: "2px",
                      }}
                    />
                  }
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.25,
                      }}
                    >
                      <Typography sx={{ fontWeight: 700 }}>
                        {item.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 4,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          whiteSpace: "pre-line",
                          wordBreak: "break-word",
                        }}
                      >
                        {item.prompt}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    flex: 1,
                    m: 0,
                    minWidth: 0,
                    alignItems: "flex-start",
                    "& .MuiFormControlLabel-label": {
                      width: "100%",
                      minWidth: 0,
                    },
                  }}
                />
                {canWrite ? (
                  <IconButton
                    tooltipText={TEMPLATES_DELETE_PROMPT}
                    disabled={Boolean(deletingID)}
                    loading={deletingID === item.id}
                    edge={false}
                    onClick={(event) => onDelete(event, item.id)}
                    sx={{ p: 0.5, mt: -0.25 }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                ) : null}
              </Box>
            ))}
          </RadioGroup>
        ) : null}
        <DialogActions sx={{ px: { xs: 3, sm: 5 }, pb: 3, mt: 0.5 }}>
          <ContainerModalActions
            hasPadding={false}
            propsSecondaryButton={{
              showButton: true,
              text: BUTTON_GO_BACK,
              onClick: () => setIsImportOpen(false),
            }}
            propsPrimaryButton={{
              showButton: true,
              text: TEMPLATES_IMPORT_SELECTED,
              disabled: !selectedPromptID,
              onClick: onImportSelected,
            }}
          />
        </DialogActions>
      </ModalBasicLayout>
    </>
  );
};

export default AnimateImagePromptField;
